"use client"

import { useState } from "react"
import { ChevronRight, type LucideIcon } from "lucide-react"
import { Link } from "react-router-dom"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"

type NavItem = {
  title: string
  url: string
  icon?: LucideIcon
  exact?: boolean
  items?: {
    title: string
    url: string
  }[]
}

type NavMainProps = {
  items: NavItem[]
  activePath: string
}

const isActivePath = (activePath: string, url: string, exact = false) => {
  if (activePath === url) return true
  if (exact) return false
  return activePath.startsWith(`${url}/`)
}

const isItemActive = (activePath: string, item: NavItem) => {
  if (isActivePath(activePath, item.url, item.exact)) return true
  return item.items?.some((subItem) => isActivePath(activePath, subItem.url)) ?? false
}

export function NavMain({ items, activePath }: NavMainProps) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Plataforma</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <SidebarMenuItem key={item.title}>
            {item.items && item.items.length > 0 ? (
              <Collapsible
                asChild
                open={isItemActive(activePath, item) || openGroups[item.title] === true}
                onOpenChange={(open) =>
                  setOpenGroups((current) => ({ ...current, [item.title]: open }))
                }
                className="group/collapsible"
              >
                <div>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip={item.title}
                      isActive={isItemActive(activePath, item)}
                    >
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActivePath(activePath, subItem.url)}
                          >
                            <Link to={subItem.url}>
                              <span>{subItem.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ) : (
              <SidebarMenuButton
                asChild
                isActive={isActivePath(activePath, item.url, item.exact)}
                tooltip={item.title}
              >
                <Link to={item.url}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
